'use client'

import { X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import type { FlowNode, NodeData, AIAgentData, OperatorData, VoicemailData, PlayMessageData, TimeCheckData } from '@/lib/pbx/flow/types'
import { AIAgentPanel } from './AIAgentPanel'
import { OperatorPanel } from './OperatorPanel'
import { VoicemailPanel } from './VoicemailPanel'
import { PlayMessagePanel } from './PlayMessagePanel'
import { TimeCheckPanel } from './TimeCheckPanel'
import { useTranslations } from "next-intl"


interface NodeConfigPanelProps {
  node: FlowNode
  onUpdate: (data: Partial<NodeData>) => void
  onClose: () => void
}

export function NodeConfigPanel({ node, onUpdate, onClose }: NodeConfigPanelProps) {
  const tUi = useTranslations("Pbx.PbxFlowEditorPanelsNodeConfigPanel")

  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const media = window.matchMedia('(max-width: 767px)')
    const update = () => setIsMobile(media.matches)
    update()
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [])

  const renderPanel = () => {
    switch (node.type) {
      case 'ai_agent':
        return <AIAgentPanel data={node.data as AIAgentData} onUpdate={onUpdate} />
      case 'operator':
        return <OperatorPanel data={node.data as OperatorData} onUpdate={onUpdate} />
      case 'voicemail':
        return <VoicemailPanel data={node.data as VoicemailData} onUpdate={onUpdate} />
      case 'play_message':
        return <PlayMessagePanel data={node.data as PlayMessageData} onUpdate={onUpdate} />
      case 'time_check':
        return <TimeCheckPanel data={node.data as TimeCheckData} onUpdate={onUpdate} />
      case 'incoming_call':
        return (
          <div className="text-sm text-muted-foreground p-4">
            <span key={tUi("thisIsTheEntryPointForIncomingCalls")}>{tUi("thisIsTheEntryPointForIncomingCalls")}</span></div>
        )
      case 'hangup':
        return (
          <div className="text-sm text-muted-foreground p-4">
            <span key={tUi("thisNodeEndsTheCallNoConfigurationNeeded")}>{tUi("thisNodeEndsTheCallNoConfigurationNeeded")}</span></div>
        )
      default:
        return (
          <div className="text-sm text-muted-foreground p-4">
            <span key={tUi("noConfigurationAvailableForThisNodeType")}>{tUi("noConfigurationAvailableForThisNodeType")}</span></div>
        )
    }
  }

  const getNodeTitle = () => {
    switch (node.type) {
      case 'incoming_call':
        return 'Incoming Call'
      case 'ai_agent':
        return 'AI Agent'
      case 'operator':
        return 'Operator'
      case 'voicemail':
        return 'Voicemail'
      case 'play_message':
        return 'Play Message'
      case 'time_check':
        return 'Time Check'
      case 'hangup':
        return 'Hangup'
      default:
        return 'Node Settings'
    }
  }

  if (isMobile) {
    return (
      <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
        <DialogContent className="left-0 top-0 flex h-[100dvh] max-h-[100dvh] w-full max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none p-0 pb-0 sm:max-h-[100dvh] sm:rounded-none sm:pb-0">
          <DialogHeader className="shrink-0 border-b p-4 pr-12 text-left">
            <DialogTitle>{getNodeTitle()}</DialogTitle>
            <DialogDescription><span key={tUi("configureThisBlockThenCloseToReturnTo")}>{tUi("configureThisBlockThenCloseToReturnTo")}</span></DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 touch-pan-y overflow-y-auto overscroll-contain pb-[calc(7rem+env(safe-area-inset-bottom))]">
            {renderPanel()}
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <div className="flex min-h-0 w-80 shrink-0 flex-col border-l bg-background">
      <div className="flex shrink-0 items-center justify-between px-4 py-3 border-b">
        <h3 className="font-semibold">{getNodeTitle()}</h3>
        <Button variant="ghost" size="icon" onClick={onClose} aria-label={tUi("closeBlockSettings")}>
          <X className="w-4 h-4" />
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {renderPanel()}
      </div>
    </div>
  )
}
